import React, { useState } from 'react';
import { Check, X, Loader2, Undo2 } from 'lucide-react';
import { settingsWriteApi } from '../lib/api';
import { Button } from './ui/button';

/**
 * The assistant proposing a settings change, and the customer accepting it.
 *
 * <p>This is the first thing the assistant does that CHANGES an account, so the design is
 * deliberately unexciting: the diff is shown in full, per setting, current value beside new value,
 * and nothing happens until this button is pressed. The server has already validated the proposal
 * in dry-run against the customer's own slot, so what is listed here is what will actually change -
 * not what the model hoped would change.
 *
 * <p>Applying goes through the ORDINARY settings write endpoint, not a private one. That is what
 * makes it recoverable: the change lands in the same history, with the same revert, as an edit made
 * by hand on the settings page. The card says so, because "the assistant changed my settings" is
 * only a comfortable sentence if undoing it is obvious.
 *
 * <p>Two numbers, and they are not the same number. The list above the button comes from the dry
 * run; what the card reports afterwards comes from the WRITE. They diverge whenever the slot moved
 * in between - somebody edited it on the settings page, or a feature one of the ids depends on was
 * switched off - and reporting the preview's count as the applied count is how a customer ends up
 * believing a change landed that did not. The same rule covers `blocked`: ids the server refused
 * are named both before the press (they were part of what the assistant offered) and after it, and
 * never quietly dropped.
 */
export default function ProposalCard({ proposal, onDone, onDismiss }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  if (!proposal) return null;

  // The server refused it outright - offer no button, just the reason.
  if (proposal.problem) {
    return (
      <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs text-muted-foreground">
        <span className="font-semibold text-warning">Could not prepare that change.</span>{' '}
        {proposal.problem}
      </div>
    );
  }

  const changes = proposal.changes || [];
  // Ids the dry run already refused - the feature behind them is switched off on this slot. The
  // server sends the key only when there is something in it.
  const blocked = proposal.blocked || [];
  if (changes.length === 0) return null;

  /** "#12 Follow limit (the follow module is off)" - whatever the server gave us to name it by. */
  const describe = (b) => `${b.label || b.field || `#${b.id}`}${b.reason ? ` (${b.reason})` : ''}`;

  if (result) {
    const nothing = result.count === 0;
    return (
      <div
        className={nothing
          ? 'rounded-lg border border-warning/30 bg-warning/5 p-3 text-xs'
          : 'rounded-lg border border-success/30 bg-success/5 p-3 text-xs'}
        data-testid="assistant-proposal-done"
      >
        {nothing ? (
          /* A write that changed nothing must not be reported as a success. It happens when the
           * slot moved between the dry run behind this card and the press - somebody edited it on
           * the settings page, or a feature the ids depend on was switched off - and "Applied 3
           * settings" over a slot that still holds the old values is the one sentence a customer
           * cannot recover from on their own. */
          <p className="font-semibold text-warning">
            Nothing was changed on {proposal.slotName}
            {result.blocked.length === 0
              ? ' - those settings already hold those values.'
              : ':'}
          </p>
        ) : (
          <p className="text-success font-semibold">
            Applied {result.count} setting{result.count === 1 ? '' : 's'} to {proposal.slotName}.
            {/* The applied count is the server's, not this card's preview. They differ when the
              * slot moved between the dry run and the press. */}
            {result.count !== result.previewed
              && ` (the preview said ${result.previewed}; ${proposal.slotName} changed in between)`}
          </p>
        )}
        {result.blocked.length > 0 && (
          <p className="mt-2 text-warning" data-testid="assistant-proposal-refused">
            {`${result.blocked.length} setting${result.blocked.length === 1 ? '' : 's'} refused: `}
            {result.blocked.map(describe).join('; ')}
          </p>
        )}
        {!nothing && (
          <p className="mt-1 flex items-center gap-1 text-muted-foreground">
            <Undo2 className="h-3 w-3" aria-hidden />
            Undo it any time from the slot&apos;s settings history.
          </p>
        )}
      </div>
    );
  }

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const values = {};
      changes.forEach((c) => { values[String(c.id)] = c.newValue; });
      const res = await settingsWriteApi.put(proposal.slotName, values, {
        // Recorded on the change, so settings history shows WHERE it came from.
        reason: 'Applied from the Undead assistant',
      });
      /*
       * Read the WRITE's own answer, never the preview's count. This card used to report
       * `changes.length` unconditionally, which meant the one case that matters - the write
       * landing fewer settings than the dry run promised, or none at all - was reported to the
       * customer as a complete success. The server always sends `changed` and `blocked`
       * (SettingsWriteController.applyResultMap), so there is nothing to infer.
       */
      const d = (res && res.data) || {};
      setResult({
        count: typeof d.changed === 'number' ? d.changed : changes.length,
        previewed: changes.length,
        blocked: Array.isArray(d.blocked) ? d.blocked : [],
      });
      if (onDone) onDone();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || e.message
        || 'Could not apply the change.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="assistant-proposal">
      <p className="font-semibold text-foreground">
        Apply this to {proposal.slotName}?
      </p>
      <ul className="mt-2 space-y-1">
        {changes.map((c) => (
          <li key={c.id} className="flex flex-wrap items-baseline gap-x-1.5">
            <span className="font-medium text-foreground">#{c.id}</span>
            <span className="text-muted-foreground">{c.field}</span>
            <span className="text-muted-foreground/70 line-through break-all">{String(c.oldValue)}</span>
            <span aria-hidden>-&gt;</span>
            <span className="font-semibold text-primary break-all">{String(c.newValue)}</span>
          </li>
        ))}
      </ul>
      {/* The other half of the proposal: ids the assistant asked for and the slot cannot take.
        * Shown ABOVE Apply, because a compound request ("turn off 806 and raise 100") that
        * silently drops one of the two leaves the customer believing both were done - the answer
        * above promised both, and this card is the only place the refusal is ever stated. */}
      {blocked.length > 0 && (
        <p className="mt-2 text-warning" data-testid="assistant-proposal-blocked">
          {`${blocked.length} other setting${blocked.length === 1 ? '' : 's'} in that request `}
          {`cannot be used on ${proposal.slotName} and ${blocked.length === 1 ? 'is' : 'are'} not included: `}
          {blocked.map(describe).join('; ')}
        </p>
      )}
      <p className="mt-2 text-muted-foreground">
        Nothing changes until you press Apply. It is recorded in the slot&apos;s settings history and
        can be reverted there.
      </p>
      {error && <p className="mt-2 text-destructive">{error}</p>}
      <div className="mt-2 flex gap-1.5">
        <Button size="sm" className="h-7 flex-1 text-[11px]" disabled={busy} onClick={apply}>
          {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Check className="mr-1 h-3 w-3" />}
          Apply
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-[11px]" disabled={busy} onClick={onDismiss}>
          <X className="mr-1 h-3 w-3" /> No thanks
        </Button>
      </div>
    </div>
  );
}
